/**
 * Driving-style analysis.
 *
 * The goal is to make aggression legible while you drive, not to hand out a
 * grade afterwards. Penalties decay, so easing off visibly recovers the score,
 * and every deduction is recorded as an event with a plain-language reason.
 */
import type { SignalDef, VehicleProfile } from '../types.js';
import type { SignalStore } from '../signals/store.js';

const S = (i: Partial<SignalDef> & Pick<SignalDef, 'key' | 'name' | 'short' | 'unit' | 'category'>): SignalDef => ({
  min: 0, max: 100, decimals: 1, betterWhen: 'neutral', ...i,
});

export const SCORE_SIGNALS: SignalDef[] = [
  S({
    key: 'score_overall', name: 'Driving Score', short: 'Score', unit: '/100', category: 'derived',
    min: 0, max: 100, decimals: 0, betterWhen: 'higher', warnBelow: 60,
    description: 'Weighted blend of the four sub-scores below. Recovers as you drive smoothly.',
  }),
  S({ key: 'score_smoothness', name: 'Smoothness', short: 'Smooth', unit: '/100', category: 'derived', min: 0, max: 100, decimals: 0, betterWhen: 'higher', description: 'Penalises hard acceleration, hard braking and sudden throttle movements.' }),
  S({ key: 'score_economy', name: 'Economy', short: 'Economy', unit: '/100', category: 'derived', min: 0, max: 100, decimals: 0, betterWhen: 'higher', description: 'Rewards low consumption for the speed you are travelling, and coasting.' }),
  S({ key: 'score_anticipation', name: 'Anticipation', short: 'Anticip.', unit: '/100', category: 'derived', min: 0, max: 100, decimals: 0, betterWhen: 'higher', description: 'Rewards holding a steady speed instead of accelerating into braking.' }),
  S({ key: 'score_engine_care', name: 'Engine Care', short: 'Care', unit: '/100', category: 'derived', min: 0, max: 100, decimals: 0, betterWhen: 'higher', description: 'Penalises revving a cold engine, over-revving, and long idling.' }),
  S({
    key: 'aggression', name: 'Live Aggression', short: 'Aggression', unit: '%', category: 'derived',
    min: 0, max: 100, decimals: 0, betterWhen: 'lower', warnAbove: 60,
    description: 'How hard you are driving right now, from pedal speed, g-force and engine speed.',
  }),
  S({
    key: 'shift_advice', name: 'Shift Advice', short: 'Shift', unit: '', category: 'derived',
    min: -1, max: 1, decimals: 0,
    enumLabels: { [-1]: 'Change down', 0: 'Hold', 1: 'Change up' },
  }),
];

export type EventKind =
  | 'harsh_accel'
  | 'harsh_brake'
  | 'throttle_stab'
  | 'over_rev'
  | 'cold_rev'
  | 'long_idle';

export interface DrivingEvent {
  t: number;
  kind: EventKind;
  /** 0..1, how bad. Drives both the deduction and the UI emphasis. */
  severity: number;
  detail: string;
}

export interface Thresholds {
  harshAccelMs2: number;
  harshBrakeMs2: number;
  throttleStabPctPerSec: number;
  idleSecondsBeforePenalty: number;
  coldEngineC: number;
}

/**
 * Calibrated against `accel_long`, which is a least-squares slope over a
 * one-second window rather than an instantaneous figure.
 *
 * That distinction matters. Acceleration ramps up and tapers off, so a launch
 * peaking near 3.4 m/s2 averages only about 2.5 over the window -- an
 * instantaneous threshold of 2.5 would therefore fire only on the very hardest
 * pulls and quietly miss ordinary aggressive driving. Braking sustains for
 * longer and is attenuated far less, so its threshold stays where it was.
 */
export const DEFAULT_THRESHOLDS: Thresholds = {
  harshAccelMs2: 2.0,
  harshBrakeMs2: -3.0,
  throttleStabPctPerSec: 110,
  idleSecondsBeforePenalty: 90,
  coldEngineC: 60,
};

interface Penalty {
  /** Accumulated deduction, 0..100. Decays exponentially. */
  amount: number;
}

/** Time constant for penalty decay: a deduction roughly halves every 90 s. */
const DECAY_HALF_LIFE_S = 90;

export class DrivingAnalyser {
  private readonly smoothness: Penalty = { amount: 0 };
  private readonly economy: Penalty = { amount: 0 };
  private readonly anticipation: Penalty = { amount: 0 };
  private readonly engineCare: Penalty = { amount: 0 };

  private events: DrivingEvent[] = [];
  private idleFor = 0;
  private lastEventAt: Partial<Record<EventKind, number>> = {};
  /** Rolling speed window for the anticipation measure. */
  private speedWindow: number[] = [];

  constructor(
    private vehicle: VehicleProfile,
    private thresholds: Thresholds = DEFAULT_THRESHOLDS,
  ) {}

  setVehicle(v: VehicleProfile): void {
    this.vehicle = v;
  }

  setThresholds(t: Thresholds): void {
    this.thresholds = t;
  }

  get recentEvents(): readonly DrivingEvent[] {
    return this.events;
  }

  reset(): void {
    this.smoothness.amount = 0;
    this.economy.amount = 0;
    this.anticipation.amount = 0;
    this.engineCare.amount = 0;
    this.events = [];
    this.idleFor = 0;
    this.speedWindow = [];
  }

  /** Register the store's own signals so scores are plottable like any other. */
  static register(store: SignalStore): void {
    for (const def of SCORE_SIGNALS) store.register(def);
  }

  update(store: SignalStore, dt: number, now: number): void {
    if (dt <= 0) return;
    const decay = Math.pow(0.5, dt / DECAY_HALF_LIFE_S);
    this.smoothness.amount *= decay;
    this.economy.amount *= decay;
    this.anticipation.amount *= decay;
    this.engineCare.amount *= decay;

    const accel = store.value('accel_long');
    const throttleRate = store.value('throttle_rate');
    const rpm = store.value('rpm');
    const speed = store.value('speed');
    const coolant = store.value('coolant_temp');
    const consumption = store.value('consumption');
    const t = this.thresholds;

    // ------------------------------------------------------------ smoothness
    if (accel !== undefined) {
      if (accel > t.harshAccelMs2) {
        const sev = Math.min((accel - t.harshAccelMs2) / 2.5, 1);
        this.smoothness.amount += sev * 14 * dt;
        this.record(now, 'harsh_accel', sev, 'Accelerated at ' + accel.toFixed(1) + ' m/s²');
      } else if (accel < t.harshBrakeMs2) {
        const sev = Math.min((t.harshBrakeMs2 - accel) / 3, 1);
        this.smoothness.amount += sev * 16 * dt;
        this.record(now, 'harsh_brake', sev, 'Braked at ' + Math.abs(accel).toFixed(1) + ' m/s²');
      }
    }
    if (throttleRate !== undefined && Math.abs(throttleRate) > t.throttleStabPctPerSec) {
      const sev = Math.min((Math.abs(throttleRate) - t.throttleStabPctPerSec) / 200, 1);
      this.smoothness.amount += sev * 10 * dt;
      this.record(now, 'throttle_stab', sev, 'Throttle moved ' + Math.abs(throttleRate).toFixed(0) + ' %/s');
    }

    // --------------------------------------------------------------- economy
    if (consumption !== undefined && speed !== undefined && speed > 15) {
      // A generous reference curve: cruising should sit near 5-7 L/100km.
      const target = 4.5 + speed * 0.02;
      if (consumption > target) {
        this.economy.amount += Math.min((consumption - target) / target, 1.5) * 5 * dt;
      }
    }
    const coasting = (store.value('accelerator') ?? 100) < 3 && (rpm ?? 0) > 900 && (speed ?? 0) > 5;
    if (coasting) this.economy.amount = Math.max(0, this.economy.amount - 3 * dt);

    // ---------------------------------------------------------- anticipation
    if (speed !== undefined) {
      this.speedWindow.push(speed);
      if (this.speedWindow.length > 120) this.speedWindow.shift();
      // Accelerating hard then braking hard soon after is the signature of not
      // reading the road ahead. Penalise the combination, not either alone.
      const recentAccel = this.eventWithin(now, 'harsh_accel', 8000);
      const recentBrake = this.eventWithin(now, 'harsh_brake', 8000);
      if (recentAccel && recentBrake) this.anticipation.amount += 12 * dt;
    }

    // ----------------------------------------------------------- engine care
    if (rpm !== undefined) {
      if (coolant !== undefined && coolant < t.coldEngineC && rpm > 2500) {
        const sev = Math.min((rpm - 2500) / 2000, 1);
        this.engineCare.amount += sev * 12 * dt;
        this.record(now, 'cold_rev', sev, rpm.toFixed(0) + ' rpm with coolant at ' + coolant.toFixed(0) + '°C');
      }
      if (rpm > this.vehicle.redlineRpm * 0.92) {
        const sev = Math.min((rpm - this.vehicle.redlineRpm * 0.92) / (this.vehicle.redlineRpm * 0.08), 1);
        this.engineCare.amount += sev * 10 * dt;
        this.record(now, 'over_rev', sev, 'Approached the limiter at ' + rpm.toFixed(0) + ' rpm');
      }

      const idling = rpm > 300 && (speed ?? 0) < 2;
      this.idleFor = idling ? this.idleFor + dt : 0;
      if (this.idleFor > t.idleSecondsBeforePenalty) {
        this.engineCare.amount += 2 * dt;
        this.record(now, 'long_idle', 0.4, 'Idled for ' + Math.round(this.idleFor) + ' s');
      }
    }

    // --------------------------------------------------------------- publish
    const smooth = clamp100(100 - this.smoothness.amount);
    const econ = clamp100(100 - this.economy.amount);
    const antic = clamp100(100 - this.anticipation.amount);
    const care = clamp100(100 - this.engineCare.amount);

    store.update('score_smoothness', smooth, now);
    store.update('score_economy', econ, now);
    store.update('score_anticipation', antic, now);
    store.update('score_engine_care', care, now);
    store.update('score_overall', smooth * 0.35 + econ * 0.3 + antic * 0.2 + care * 0.15, now);
    store.update('aggression', this.liveAggression(accel, throttleRate, rpm), now);
    store.update('shift_advice', this.shiftAdvice(rpm, store.value('gear'), throttleRate), now);
  }

  /**
   * Instantaneous "how hard am I driving" figure, independent of the decaying
   * scores. Useful as a live bar that reacts immediately.
   */
  private liveAggression(
    accel: number | undefined,
    throttleRate: number | undefined,
    rpm: number | undefined,
  ): number {
    const a = Math.min(Math.abs(accel ?? 0) / 3.5, 1);
    const tr = Math.min(Math.abs(throttleRate ?? 0) / 200, 1);
    const r = Math.min(Math.max((rpm ?? 0) - this.vehicle.ecoShiftRpm, 0) / (this.vehicle.redlineRpm - this.vehicle.ecoShiftRpm), 1);
    return clamp100((a * 0.45 + tr * 0.3 + r * 0.25) * 100);
  }

  /**
   * Suggest an upshift when the engine is spinning past the economy band under
   * light load, and a downshift when it is labouring.
   */
  private shiftAdvice(rpm: number | undefined, gear: number | undefined, throttleRate: number | undefined): number {
    // Zero is neutral and negative means the gear is not known yet.
    if (rpm === undefined || gear === undefined || gear <= 0) return 0;
    const gentle = Math.abs(throttleRate ?? 0) < 60;
    if (rpm > this.vehicle.ecoShiftRpm && gentle && gear < this.vehicle.gearCount) return 1;
    if (rpm < 1200 && gear > 1) return -1;
    return 0;
  }

  /** Rate-limit repeated events of the same kind so the log stays readable. */
  private record(now: number, kind: EventKind, severity: number, detail: string): void {
    const last = this.lastEventAt[kind] ?? 0;
    if (now - last < 3000) return;
    this.lastEventAt[kind] = now;
    this.events.push({ t: now, kind, severity, detail });
    if (this.events.length > 200) this.events.shift();
  }

  private eventWithin(now: number, kind: EventKind, windowMs: number): boolean {
    const last = this.lastEventAt[kind];
    return last !== undefined && now - last < windowMs;
  }
}

const clamp100 = (v: number): number => Math.max(0, Math.min(100, v));

export const EVENT_LABELS: Record<EventKind, string> = {
  harsh_accel: 'Hard acceleration',
  harsh_brake: 'Hard braking',
  throttle_stab: 'Abrupt throttle',
  over_rev: 'Over-revving',
  cold_rev: 'Revving a cold engine',
  long_idle: 'Extended idling',
};
